import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  TextField,
  IconButton,
} from "@mui/material";
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  ExpandLess,
  ExpandMore,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Visibility,
  VisibilityOff,
  Autorenew,
} from "@mui/icons-material";
import { spacing } from "@mui/system";

// Import the profile pages
import AccountInfo from "./AccountInfo";
import ChangePassword from "./ChangePassword";
import MfaSettings from "./MfaSettings";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const SidebarContainer = styled(Box)(({ theme }) => ({
  height: "fit-content",
  position: "sticky",
  top: theme.spacing(2),
}));

// Password generator helper
const easyWords = [
  "Welcome",
  "Table",
  "Water",
  "Garden",
  "Rocket",
  "Summer",
  "Winter",
  "Coffee",
  "Laptop",
  "Orange",
  "River",
  "Forest",
  "Travel",
  "Dinner",
  "Music",
  "Dream",
  "Ocean",
  "Light",
  "Happy",
  "Magic",
];
const specialChars = ["#", "@", "!", "$", "%", "*"];
const numbers = ["12", "2024", "99", "42", "77", "88", "01", "09", "33", "55"];

const generateEasyPassword = () => {
  const word = easyWords[Math.floor(Math.random() * easyWords.length)];
  const char = specialChars[Math.floor(Math.random() * specialChars.length)];
  const num = numbers[Math.floor(Math.random() * numbers.length)];
  // Optionally, mix up the case of a letter in the word
  let mixedWord = word;
  if (word.length > 2 && Math.random() > 0.5) {
    const idx = Math.floor(Math.random() * (word.length - 2)) + 1;
    mixedWord = word.slice(0, idx) + (Math.random() > 0.5 ? word[idx].toLowerCase() : word[idx].toUpperCase()) + word.slice(idx + 1);
  }
  return `${mixedWord}${char}${num}`;
};

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState("account");
  const [securityOpen, setSecurityOpen] = useState(true);

  useEffect(() => {
    const viewParam = new URLSearchParams(location.search).get("view");
    if (viewParam === "account" || viewParam === "mfa" || viewParam === "password") {
      setCurrentView(viewParam);
      return;
    }

    if (location.state?.view === "account" || location.state?.view === "mfa" || location.state?.view === "password") {
      setCurrentView(location.state.view);
    }
  }, [location.search, location.state]);

  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Password change handlers
  const handlePasswordInputChange = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPasswordError("");
    setPasswordSuccess(false);
  };

  const handlePasswordSubmit = async () => {
    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return;
    }

    try {
      setPasswordLoading(true);
      setPasswordError("");

      const token = sessionStorage.getItem("accessToken");
      console.log("Token exists:", !!token);

      if (!token) {
        setPasswordError("Authentication token not found. Please log in again.");
        return;
      }

      const response = await fetch("/api/manage/info", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          newEmail: "", // Keep current email
          newPassword: passwordForm.newPassword,
          oldPassword: passwordForm.currentPassword,
        }),
      });

      console.log("Response status:", response.status);

      if (response.ok) {
        const result = await response.json();
        console.log("Success response:", result);
        setPasswordSuccess(true);
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Error response:", response.status, errorData);
        setPasswordError(errorData.message || `Failed to update password (${response.status}). Please check your current password.`);
      }
    } catch (err) {
      console.error("Error updating password:", err);
      setPasswordError("Failed to update password. Please try again.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError("");
    setPasswordSuccess(false);
  };

  const menuItems = [
    {
      title: "Account Information",
      icon: PersonIcon,
      view: "account",
      children: null,
    },
    {
      title: "Security Settings",
      icon: SecurityIcon,
      view: null,
      children: [
        {
          title: "MFA Settings",
          icon: ShieldIcon,
          view: "mfa",
        },
        {
          title: "Change Password",
          icon: SecurityIcon,
          view: "password",
        },
      ],
    },
  ];

  const handleMenuClick = (item) => {
    if (item.children) {
      setSecurityOpen(!securityOpen);
    } else if (item.view) {
      setCurrentView(item.view);
      navigate(`/profile?view=${item.view}`);
    }
  };

  const isActive = (view) => {
    return currentView === view;
  };

  const isParentActive = (item) => {
    if (item.children) {
      return item.children.some((child) => currentView === child.view);
    }
    return false;
  };

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const renderContent = () => {
    switch (currentView) {
      case "account":
        return <AccountInfo />;
      case "mfa":
        return <MfaSettings />;
      case "password":
        return <ChangePassword />;
      default:
        return (
          <Box>
            <Typography variant="h4">Account Information</Typography>
            <Typography variant="body1">This is the default Account Information page.</Typography>
          </Box>
        );
    }
  };

  return (
    <React.Fragment>
      <Helmet title="Profile" />

      <Typography variant="h3" gutterBottom>
        Profile Settings
      </Typography>

      <Grid container spacing={6}>
        {/* Left Sidebar Menu */}
        <Grid item xs={12} md={3}>
          <SidebarContainer>
            <Card>
              <CardContent p={0}>
                <List component="nav" disablePadding>
                  {menuItems.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItemButton
                        onClick={() => handleMenuClick(item)}
                        selected={isActive(item.view) || isParentActive(item)}
                        sx={{
                          py: 2,
                          px: 3,
                          "&.Mui-selected": {
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                            "& .MuiListItemIcon-root": {
                              color: "primary.contrastText",
                            },
                          },
                        }}
                      >
                        <ListItemIcon>
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.title} />
                        {item.children && (securityOpen ? <ExpandLess /> : <ExpandMore />)}
                      </ListItemButton>

                      {item.children && (
                        <Collapse in={securityOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {item.children.map((child, childIndex) => (
                              <ListItemButton
                                key={childIndex}
                                onClick={() => handleMenuClick(child)}
                                selected={isActive(child.view)}
                                sx={{
                                  pl: 6,
                                  py: 1.5,
                                  "&.Mui-selected": {
                                    backgroundColor: "primary.light",
                                    color: "primary.contrastText",
                                    "&:hover": {
                                      backgroundColor: "primary.main",
                                    },
                                    "& .MuiListItemIcon-root": {
                                      color: "primary.contrastText",
                                    },
                                  },
                                }}
                              >
                                <ListItemIcon>
                                  <child.icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={child.title} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      )}

                      {index < menuItems.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </SidebarContainer>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={9}>
          <Box>{renderContent()}</Box>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default Profile;
